import {useState, useContext} from 'react';

import { genericPOSTDatabaseCall, genericDELETEDatabaseCall, renderFormRow } from '../../helperFunctions.jsx';
import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx';
import { industryDetails, entityRenderFields } from '../../constants.jsx';

async function addEntityToDatabase(entity) {
  return genericPOSTDatabaseCall('/api/cfi/addEntity', { entity }, 'entity');
}

async function saveEntityToDatabase(entity) {
    return genericPOSTDatabaseCall('/api/cfi/saveEntity', { entity }, 'entity info');
}

async function deleteEntityFromDatabase(entityId) {
    return genericDELETEDatabaseCall('/api/cfi/deleteEntity', { entityId }, 'entity');
}

export function EntityDetails({ entity, setEntities, messageApi, editable = true, setEntitySelectedID, spotRates }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const saveEntity = async (entity) => {
        setSaving(true);
        messageApi.loading('Saving entity info...', 0);
        try {
            await saveEntityToDatabase(entity);
            messageApi.destroy();
            messageApi.success('Entity info saved successfully');
        } catch (error) {
            console.error('Error saving entity info:', error);
            messageApi.destroy();
            messageApi.error('Failed to save entity info');
        }
        setSaving(false);
    }

    const deleteEntity = async (entityId) => {
        setDeleting(true);
        messageApi.loading('Deleting entity...', 0);
        try {
            await deleteEntityFromDatabase(entityId);
            setEntities(prevEntities => prevEntities.filter(ent => ent.id !== entityId));
            setEntitySelectedID(""); // Clear selected entity if it was deleted
            messageApi.destroy();
            messageApi.success('Entity deleted successfully');
        } catch (error) {
            console.error('Error deleting entity:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete entity');
        }
        setDeleting(false);
    }

    const handleInputChange = (event, changedEntity, key, field) => {
        let newValue = event.target.value;
        const updatedEntityBase = { ...changedEntity, [key]: newValue };

        if (key === 'industry') {
            const keys = Object.keys(industryDetails[newValue]);

            for (const k of keys) {
                if (updatedEntityBase[k] === undefined) continue; // Skip if not defined for entity which means its was prolly for financials builder
                updatedEntityBase[k] = industryDetails[newValue][k];
            }
        }

        if (key === 'in_currency' || key === 'out_currency') {
            // Update spot rate based on currency change
            const conversionKey = updatedEntityBase.in_currency + '_' + updatedEntityBase.out_currency;
            const value = spotRates[conversionKey] || '1.000';

            updatedEntityBase.spot_rate = value;
        }

        setEntities(prevEntities => {
            return prevEntities.map(entity => {
                return entity.id === updatedEntityBase.id ? updatedEntityBase : entity;
            });
        });
    }

    const handleInputBlur = (event, changedEntity, key, field) => {
        let newValue = event.target.value;

        if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
            messageApi.error(field.errorMessage);
            newValue = field.default || ''; // Use default value if provided
        }

        if (newValue === '') {
            messageApi.error('This field cannot be empty');
            newValue = field.default || ''; // Use default value if provided
        }

        setEntities(prevEntities => {
            return prevEntities.map(entity => {
                if (entity.id === changedEntity.id) {
                    return { ...entity, [key]: newValue };
                }
                return entity;
            });
        });
    }

    return (
        <div key={entity.id} className="entity-card">
            {entityRenderFields.map(field => renderFormRow(field, entity, handleInputChange, editable, handleInputBlur))}
            <div className="form-row">
                <label htmlFor={"spot_rate_" + entity.id}>Spot Rate</label>
                <input id={"spot_rate_" + entity.id} type="text" value={entity.spot_rate} className="form-input" readOnly></input>
            </div>
            {!editable ? <></> : (
            <div className="flex-row bottom-actions">
                <div className="form-row actions-row">
                    <button type='button' disabled={deleting} onClick={() => deleteEntity(entity.id)} className="delete-button">Delete Entity</button>
                </div>
                <div className="form-row actions-row">
                    <button type='button' disabled={saving} onClick={() => saveEntity(entity)} className="save-button">Save Entity</button>
                </div>
            </div>
            )}
        </div>
    )
}

function EntitiesPage() {
    const { entities: contextEntities, setEntities, setPageNum, messageApi, entitySelectedID, setEntitySelectedID, spotRates } = useContext(CriticalFinancePageContext);
    const [newEntityName, setNewEntityName] = useState('');
    
    const addEntity = async () => {
        if (!newEntityName) {
            messageApi.error('Please enter a entity name');
            return;
        }
        messageApi.loading('Adding entity...', 0);
        try {
            const newEntity = await addEntityToDatabase({ name: newEntityName });
            // Update company stuff before adding to state
            const keys = Object.keys(industryDetails[newEntity.industry])
            for (const k of keys) {
                if (newEntity[k] === undefined) continue; // Skip if not defined for entity which means its was prolly for financials builder
                newEntity[k] = industryDetails[newEntity.industry][k];
            }

            setEntities(prevEntities => [...prevEntities, newEntity]);
            setNewEntityName(''); // Clear input field
            messageApi.destroy();
            messageApi.success('Entity added successfully');
        } catch (error) {
            console.error('Error adding entity:', error);
            messageApi.destroy();
            messageApi.error('Failed to add entity');
        }
    }

    return (
        <div className="financial-builder-container entity-industry-container">
            <h2>All Entities</h2>
            <div>
                {contextEntities.map(entity => (
                    <EntityDetails key={entity.id} spotRates={spotRates} setEntitySelectedID={setEntitySelectedID} messageApi={messageApi} setEntities={setEntities} entity={entity} />
                ))}
            </div>
            <div className="add-entity-controls form-section">
                <div className="form-row">
                    <label htmlFor="new_entity_name">Add New Entity</label>
                    <input type="text" className="form-input" value={newEntityName} onChange={(e) => setNewEntityName(e.target.value)} placeholder="Enter entity name" id="new_entity_name" />
                </div>
                <button className="add-button" onClick={addEntity}>ADD Entity</button>
            </div>
            <div className="form-section">
                <div className="form-row">
                    <label htmlFor="select_entity_for_next">Select Entity</label>
                    <select id="select_entity_for_next" className="form-input" value={entitySelectedID || ''} onChange={(e) => setEntitySelectedID(e.target.value)}>
                        <option value="" disabled hidden>-- Select a entity --</option>
                        {contextEntities.map(entity => (
                            <option key={entity.id} value={entity.id}>{entity.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="navigation-buttons">
                <button type="button" disabled={entitySelectedID === "" || contextEntities.length === 0} onClick={() => setPageNum(2)} className="next-button">Next</button>
            </div>
        </div>
    );
}

export default EntitiesPage;